/**
 * COACombinations.jsx — AOS-FIN-COA-04: Account Combination Policy & Advisory Validation
 *
 * Three sections:
 *   1. Dimension Applicability Matrix — all dims × all account types
 *   2. Rule Management — CRUD for coa_segment_rule
 *   3. Combination Validator — account picker + dimension selectors + VALID/INVALID result
 *
 * ADVISORY ONLY — no GL enforcement.
 *
 * RBAC:
 *   Read:    finance.coa.read  (view matrix, run validator)
 *   Manage:  finance.coa.segment.manage  (create, update, deactivate rules)
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useInstitution } from "../context/InstitutionContext";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, Plus, RefreshCw, Edit3, Building2,
  CheckCircle2, XCircle, MinusCircle, HelpCircle, ChevronDown,
  Search, Layers, ArrowRight, Info, ToggleLeft, ToggleRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "NET_ASSET", "REVENUE", "EXPENSE"];
const RULE_TYPES = ["REQUIRED", "OPTIONAL", "FORBIDDEN"];
const MANAGE_ROLES = new Set(["super_admin", "institution_admin", "cfo"]);

const RULE_META = {
  REQUIRED: {
    label: "Required",
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle2,
    iconClass: "text-emerald-600",
  },
  OPTIONAL: {
    label: "Optional",
    color: "bg-slate-100 text-slate-600 border-slate-200",
    icon: MinusCircle,
    iconClass: "text-slate-400",
  },
  FORBIDDEN: {
    label: "Forbidden",
    color: "bg-rose-100 text-rose-700 border-rose-200",
    icon: XCircle,
    iconClass: "text-rose-500",
  },
};

const SOURCE_META = {
  RULE: { badge: "Rule", color: "text-indigo-600" },
  GLOBAL_MANDATORY: { badge: "Global", color: "text-amber-600" },
  DEFAULT: { badge: "Default", color: "text-slate-400" },
};

function RuleBadge({ ruleType, source }) {
  const meta = RULE_META[ruleType] || RULE_META.OPTIONAL;
  const Icon = meta.icon;
  const sm = SOURCE_META[source] || SOURCE_META.DEFAULT;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${meta.color}`}>
        <Icon className={`w-3 h-3 ${meta.iconClass}`} />
        {meta.label}
      </div>
      <span className={`text-[10px] ${sm.color}`}>{sm.badge}</span>
    </div>
  );
}

// ── Rule Create/Edit Dialog ────────────────────────────────────────────────

function RuleDialog({ open, onClose, onSave, dimensions, initial, presetDimId, presetAcctType }) {
  const isEdit = !!initial;
  const [dimId, setDimId] = useState(initial?.dimension_id ?? presetDimId ?? "");
  const [acctType, setAcctType] = useState(initial?.account_type ?? presetAcctType ?? "ASSET");
  const [ruleType, setRuleType] = useState(initial?.rule_type ?? "OPTIONAL");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDimId(initial?.dimension_id ?? presetDimId ?? "");
      setAcctType(initial?.account_type ?? presetAcctType ?? "ASSET");
      setRuleType(initial?.rule_type ?? "OPTIONAL");
    }
  }, [open, initial, presetDimId, presetAcctType]);

  const handleSave = async () => {
    if (!dimId) { toast.error("Select a dimension."); return; }
    setSaving(true);
    try {
      await onSave({ dimension_id: dimId, account_type: acctType, rule_type: ruleType });
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const selectedDim = dimensions.find(d => d.dimension_id === dimId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="rule-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Update Rule" : "Create Segment Rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Dimension <span className="text-red-500">*</span></Label>
            <Select value={dimId} onValueChange={setDimId} disabled={isEdit}>
              <SelectTrigger data-testid="rule-dim-select">
                <SelectValue placeholder="Select dimension…" />
              </SelectTrigger>
              <SelectContent>
                {dimensions.map(d => (
                  <SelectItem key={d.dimension_id} value={d.dimension_id}>
                    {d.dimension_code} — {d.dimension_name}
                    {d.is_mandatory ? " (global mandatory)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDim?.is_mandatory && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Global mandatory dim — only REQUIRED is permitted (INV-COA-04-03)
              </p>
            )}
          </div>
          <div>
            <Label>Account Type <span className="text-red-500">*</span></Label>
            <Select value={acctType} onValueChange={setAcctType} disabled={isEdit}>
              <SelectTrigger data-testid="rule-accttype-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {isEdit && <p className="text-xs text-slate-400 mt-1">Account type is immutable after creation.</p>}
          </div>
          <div>
            <Label>Rule Type <span className="text-red-500">*</span></Label>
            <Select value={ruleType} onValueChange={setRuleType}>
              <SelectTrigger data-testid="rule-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RULE_TYPES.map(t => (
                  <SelectItem
                    key={t} value={t}
                    disabled={selectedDim?.is_mandatory && t !== "REQUIRED"}
                  >
                    {t} {selectedDim?.is_mandatory && t !== "REQUIRED" ? "— not allowed for global mandatory dims" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="rule-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update Rule" : "Create Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validator section ──────────────────────────────────────────────────────

function CombinationValidator({ instId, coaId, token }) {
  const [accounts, setAccounts] = useState([]);
  const [dims, setDims] = useState([]);
  const [dimValues, setDimValues] = useState({});
  const [accountSearch, setAccountSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [assignments, setAssignments] = useState({}); // dim_id → value_id
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const authHdr = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  useEffect(() => {
    if (!instId) return;
    // Load accounts using COA activations endpoint
    fetch(`${API}/api/finance/coa/activations/accounts/list?institution_id=${instId}`, { headers: authHdr() })
      .then(r => r.ok ? r.json() : []).then(d => setAccounts(Array.isArray(d) ? d : d.items || [])).catch(() => {});
    // Load dims from COA-03 endpoint
    fetch(`${API}/api/finance/coa/segments?institution_id=${instId}&status=ACTIVE`, { headers: authHdr() })
      .then(r => r.ok ? r.json() : []).then(d => {
        setDims(d);
        d.forEach(dim => {
          fetch(`${API}/api/finance/coa/segments/${dim.id}/values?institution_id=${instId}&status=ACTIVE`, { headers: authHdr() })
            .then(r => r.ok ? r.json() : []).then(vals => {
              setDimValues(prev => ({ ...prev, [dim.id]: vals }));
            }).catch(() => {});
        });
      }).catch(() => {});
  }, [instId]);

  const filteredAccounts = accounts.filter(a =>
    a.account_code?.toLowerCase().includes(accountSearch.toLowerCase()) ||
    a.name?.toLowerCase().includes(accountSearch.toLowerCase())
  );

  const handleValidate = async () => {
    if (!selectedAccount) { toast.error("Select an account first."); return; }
    setValidating(true);
    setResult(null);
    try {
      const dimAssignments = Object.entries(assignments)
        .filter(([_, vId]) => vId)
        .map(([dId, vId]) => ({ dimension_id: dId, dimension_value_id: vId }));
      const r = await fetch(
        `${API}/api/finance/coa/combinations/validate?institution_id=${instId}&chart_of_accounts_id=${coaId}`,
        {
          method: "POST",
          headers: authHdr(),
          body: JSON.stringify({ account_id: selectedAccount.id, dimension_assignments: dimAssignments }),
        }
      );
      if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
      setResult(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally { setValidating(false); }
  };

  return (
    <div className="space-y-4" data-testid="combination-validator">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
        <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          <strong>Advisory Only</strong> — This validator checks combination rules but does NOT prevent journal creation.
          GL enforcement requires separate owner authorization.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Account picker */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Label className="mb-2 block font-medium">1. Select Account</Label>
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search by code or name…"
              value={accountSearch}
              onChange={e => setAccountSearch(e.target.value)}
              data-testid="account-search-input"
            />
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
            {filteredAccounts.length === 0 ? (
              <p className="text-xs text-slate-400 p-3 text-center">No accounts found.</p>
            ) : filteredAccounts.slice(0, 20).map(a => (
              <button
                key={a.id}
                data-testid={`account-option-${a.account_code}`}
                onClick={() => { setSelectedAccount(a); setResult(null); }}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 text-sm ${
                  selectedAccount?.id === a.id ? "bg-indigo-50 font-medium" : ""
                }`}
              >
                <span className="font-mono text-xs text-slate-500 mr-2">{a.account_code}</span>
                {a.name}
                <span className={`ml-2 text-xs px-1.5 rounded ${
                  a.account_type === "EXPENSE" ? "bg-orange-100 text-orange-700" :
                  a.account_type === "REVENUE" ? "bg-green-100 text-green-700" :
                  "bg-slate-100 text-slate-600"
                }`}>{a.account_type}</span>
              </button>
            ))}
          </div>
          {selectedAccount && (
            <div className="mt-2 p-2 bg-indigo-50 rounded text-xs text-indigo-700">
              Selected: <strong>{selectedAccount.account_code}</strong> — {selectedAccount.name}
              <span className="ml-1">({selectedAccount.account_type})</span>
            </div>
          )}
        </div>

        {/* Dimension assignments */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Label className="mb-2 block font-medium">2. Assign Dimension Values</Label>
          {dims.length === 0 ? (
            <p className="text-xs text-slate-400">No dimensions configured. Set them up in Segment Config first.</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {dims.map(dim => (
                <div key={dim.id} className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-600 w-20 truncate" title={dim.name}>
                    {dim.code}
                  </span>
                  <Select
                    value={assignments[dim.id] || "__none__"}
                    onValueChange={v => {
                      setAssignments(prev => ({ ...prev, [dim.id]: v === "__none__" ? null : v }));
                      setResult(null);
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid={`dim-assign-${dim.code}`}>
                      <SelectValue placeholder="No value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {(dimValues[dim.id] || []).filter(v => v.status === "ACTIVE").map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.code} — {v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Button
        onClick={handleValidate}
        disabled={!selectedAccount || validating}
        className="w-full"
        data-testid="validate-btn"
      >
        {validating ? "Validating…" : "Check Combination"}
        {!validating && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>

      {/* Validation result */}
      {result && (
        <div
          className={`rounded-xl border p-5 ${
            result.result === "VALID"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-rose-50 border-rose-200"
          }`}
          data-testid="validation-result"
        >
          <div className="flex items-center gap-3 mb-3">
            {result.result === "VALID"
              ? <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              : <XCircle className="w-6 h-6 text-rose-600" />
            }
            <div>
              <p className={`font-semibold text-lg ${result.result === "VALID" ? "text-emerald-800" : "text-rose-800"}`}>
                {result.result}
              </p>
              <p className="text-xs text-slate-500">
                {result.account_code} · {result.account_name} · {result.account_type}
              </p>
            </div>
          </div>

          {result.missing_required.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-rose-700 mb-1">Missing Required:</p>
              {result.missing_required.map((m, i) => (
                <div key={i} className="text-xs text-rose-600 bg-rose-100 rounded px-2 py-1 mb-1">
                  <strong>{m.dimension_name}</strong> — {m.remedy}
                </div>
              ))}
            </div>
          )}

          {result.forbidden_present.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-rose-700 mb-1">Forbidden Present:</p>
              {result.forbidden_present.map((f, i) => (
                <div key={i} className="text-xs text-rose-600 bg-rose-100 rounded px-2 py-1 mb-1">
                  <strong>{f.dimension_name}</strong> — {f.remedy}
                </div>
              ))}
            </div>
          )}

          {result.inactive_values.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-amber-700 mb-1">Inactive Values:</p>
              {result.inactive_values.map((v, i) => (
                <div key={i} className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1 mb-1">
                  {v.dimension_name}: <strong>{v.dimension_value_code}</strong> — {v.issue}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-current border-opacity-20">
            <p className="text-xs text-slate-500">
              Rule version: {result.effective_rule_version} · Evaluated: {new Date(result.evaluated_at).toLocaleString()}
            </p>
            <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
              <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {result.advisory_note}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rules management table ─────────────────────────────────────────────────

function RulesTable({ rules, canManage, onEdit, onToggle }) {
  if (rules.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400">
        <Layers className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <p className="text-sm">No rules configured.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="rules-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wider">
            <th className="text-left px-4 py-2">Dimension</th>
            <th className="text-left px-4 py-2">Account Type</th>
            <th className="text-left px-4 py-2">Rule</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Updated by</th>
            {canManage && <th className="text-right px-4 py-2">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rules.map(r => (
            <tr key={r.id} data-testid={`rule-row-${r.dimension_code || r.dimension_id}-${r.account_type}`}
              className={`hover:bg-slate-50 ${r.status === "INACTIVE" ? "opacity-50" : ""}`}>
              <td className="px-4 py-2">
                <div>
                  <span className="font-medium text-slate-800">{r.dimension_name || r.dimension_id}</span>
                  {r.dimension_is_mandatory && (
                    <span className="ml-1 text-xs text-amber-600 bg-amber-50 px-1.5 rounded">Global</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-2">
                <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{r.account_type}</span>
              </td>
              <td className="px-4 py-2">
                <RuleBadge ruleType={r.rule_type} source="RULE" />
              </td>
              <td className="px-4 py-2">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  r.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}>{r.status}</span>
              </td>
              <td className="px-4 py-2 text-xs text-slate-400">{r.updated_by_email}</td>
              {canManage && (
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2"
                      onClick={() => onEdit(r)} data-testid={`edit-rule-btn-${r.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2"
                      onClick={() => onToggle(r)} data-testid={`toggle-rule-btn-${r.id}`}>
                      {r.status === "ACTIVE"
                        ? <ToggleLeft className="w-3.5 h-3.5 text-rose-400" />
                        : <ToggleRight className="w-3.5 h-3.5 text-emerald-500" />}
                    </Button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function COACombinations() {
  const { user, getToken } = useAuth();
  const { selectedInstitution } = useInstitution();

  const canManage = MANAGE_ROLES.has(user?.role);
  const instId = user?.role === "super_admin"
    ? (selectedInstitution?.id || null)
    : (user?.institution_id || null);

  const [coaList, setCoaList] = useState([]);
  const [selectedCoa, setSelectedCoa] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("matrix"); // matrix | rules | validator

  // Dialog state
  const [showRuleCreate, setShowRuleCreate] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [presetDimId, setPresetDimId] = useState(null);
  const [presetAcctType, setPresetAcctType] = useState(null);

  // Matrix dimensions (for rule dialog)
  const matrixDims = matrix?.matrix || [];

  const authHeader = useCallback(async () => {
    const token = await getToken?.() || localStorage.getItem("academiaos_token");
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  }, [getToken]);

  const getToken_ = useCallback(async () => {
    return await getToken?.() || localStorage.getItem("academiaos_token");
  }, [getToken]);

  const loadCoas = useCallback(async () => {
    if (!instId) return;
    const headers = await authHeader();
    try {
      const r = await fetch(`${API}/api/finance/coa/activations/institution-coas?institution_id=${instId}`, { headers });
      if (!r.ok) return;
      const data = await r.json();
      const items = data.items || data || [];
      setCoaList(items);
      if (items.length > 0 && !selectedCoa) setSelectedCoa(items[0]);
    } catch {}
  }, [instId, authHeader, selectedCoa]);

  const loadMatrix = useCallback(async () => {
    if (!instId || !selectedCoa) return;
    setLoading(true);
    const headers = await authHeader();
    try {
      const [mResp, rResp] = await Promise.all([
        fetch(`${API}/api/finance/coa/combinations/matrix?institution_id=${instId}&chart_of_accounts_id=${selectedCoa.id}`, { headers }),
        fetch(`${API}/api/finance/coa/combinations/rules?institution_id=${instId}&chart_of_accounts_id=${selectedCoa.id}&status=ALL`, { headers }),
      ]);
      if (mResp.ok) setMatrix(await mResp.json());
      if (rResp.ok) setRules(await rResp.json());
    } finally { setLoading(false); }
  }, [instId, selectedCoa, authHeader]);

  useEffect(() => { loadCoas(); }, [instId]);
  useEffect(() => { if (selectedCoa) loadMatrix(); }, [selectedCoa]);

  const createRule = async (payload) => {
    const headers = await authHeader();
    const r = await fetch(`${API}/api/finance/coa/combinations/rules?institution_id=${instId}&chart_of_accounts_id=${selectedCoa.id}`, {
      method: "POST", headers, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Rule created.");
    await loadMatrix();
  };

  const updateRule = async (id, payload) => {
    const headers = await authHeader();
    const r = await fetch(`${API}/api/finance/coa/combinations/rules/${id}?institution_id=${instId}`, {
      method: "PATCH", headers, body: JSON.stringify({ rule_type: payload.rule_type }),
    });
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Rule updated.");
    await loadMatrix();
  };

  const toggleRule = async (rule) => {
    const action = rule.status === "ACTIVE" ? "deactivate" : "reactivate";
    const headers = await authHeader();
    const r = await fetch(`${API}/api/finance/coa/combinations/rules/${rule.id}/${action}?institution_id=${instId}`, {
      method: "POST", headers,
    });
    if (!r.ok) { toast.error((await r.json()).detail); return; }
    toast.success(`Rule ${action === "deactivate" ? "deactivated" : "reactivated"}.`);
    await loadMatrix();
  };

  if (!instId) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="coa-combinations-page">
        <div className="text-center text-slate-500">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Select an institution</p>
          <p className="text-sm mt-1">Use the institution switcher to choose an institution.</p>
        </div>
      </div>
    );
  }

  const token_ = localStorage.getItem("academiaos_token") || "";

  return (
    <div className="h-full flex flex-col" data-testid="coa-combinations-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-slate-900">Account Combination Policy</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium">
                Advisory Only
              </span>
            </div>
            <p className="text-xs text-slate-500">COA-04 · Dimension applicability rules per account type</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {coaList.length > 1 && (
            <Select value={selectedCoa?.id || ""} onValueChange={id => setSelectedCoa(coaList.find(c => c.id === id))}>
              <SelectTrigger className="h-8 text-xs w-48" data-testid="coa-select">
                <SelectValue placeholder="Select COA…" />
              </SelectTrigger>
              <SelectContent>
                {coaList.map(c => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {selectedCoa && (
            <span className="text-xs text-slate-500 hidden md:block">
              {selectedCoa.code} — {selectedCoa.name}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={loadMatrix} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {canManage && selectedCoa && (
            <Button size="sm" onClick={() => { setPresetDimId(null); setPresetAcctType(null); setShowRuleCreate(true); }}
              data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-1" /> New Rule
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-6">
        {[
          { key: "matrix", label: "Applicability Matrix" },
          { key: "rules", label: `Rules (${rules.filter(r => r.status === "ACTIVE").length})` },
          { key: "validator", label: "Combination Validator" },
        ].map(tab => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-violet-500 text-violet-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
        {!selectedCoa ? (
          <div className="text-center py-16 text-slate-400">
            <p>No Chart of Accounts found for this institution.</p>
          </div>
        ) : activeTab === "matrix" ? (
          <MatrixView
            matrix={matrix}
            loading={loading}
            canManage={canManage}
            onCreateRule={(dimId, acctType) => {
              setPresetDimId(dimId);
              setPresetAcctType(acctType);
              setShowRuleCreate(true);
            }}
            onEditRule={rule => setEditRule(rule)}
            onToggleRule={toggleRule}
            rules={rules}
          />
        ) : activeTab === "rules" ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="font-medium text-slate-800">All Segment Rules</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Effective rules for dimension applicability by account type. Advisory only.
              </p>
            </div>
            <RulesTable rules={rules} canManage={canManage} onEdit={setEditRule} onToggle={toggleRule} />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-violet-600" />
              Combination Validator
            </h3>
            <CombinationValidator instId={instId} coaId={selectedCoa.id} token={token_} />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <RuleDialog
        open={showRuleCreate}
        onClose={() => setShowRuleCreate(false)}
        onSave={createRule}
        dimensions={matrixDims.map(d => ({
          dimension_id: d.dimension_id, dimension_code: d.dimension_code,
          dimension_name: d.dimension_name, is_mandatory: d.is_mandatory,
        }))}
        presetDimId={presetDimId}
        presetAcctType={presetAcctType}
      />
      <RuleDialog
        open={!!editRule}
        onClose={() => setEditRule(null)}
        onSave={payload => updateRule(editRule.id, payload)}
        dimensions={matrixDims.map(d => ({
          dimension_id: d.dimension_id, dimension_code: d.dimension_code,
          dimension_name: d.dimension_name, is_mandatory: d.is_mandatory,
        }))}
        initial={editRule}
      />
    </div>
  );
}

// ── Matrix View ────────────────────────────────────────────────────────────

function MatrixView({ matrix, loading, canManage, onCreateRule, onEditRule, onToggleRule, rules }) {
  const ruleIndex = {};
  rules.forEach(r => {
    const key = `${r.dimension_id}:${r.account_type}`;
    ruleIndex[key] = r;
  });

  if (loading || !matrix) {
    return (
      <div className="text-center py-16 text-slate-400">
        {loading ? "Loading matrix…" : "No matrix data."}
      </div>
    );
  }

  const { account_types: atypes, matrix: rows } = matrix;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-600 space-y-0.5">
          <p><strong>RULE</strong> — explicit rule configured for this dimension × account type.</p>
          <p><strong>GLOBAL</strong> — dimension is globally mandatory (financial_dimension.is_mandatory=true); always REQUIRED.</p>
          <p><strong>DEFAULT</strong> — no rule configured; inherits dimension-level default (OPTIONAL for non-mandatory dims).</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="applicability-matrix">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-4 py-3 text-slate-600 font-semibold w-40">Dimension</th>
              {atypes.map(at => (
                <th key={at} className="px-3 py-3 text-center text-slate-600 font-semibold">
                  <span className="font-mono">{at}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(row => (
              <tr key={row.dimension_id} data-testid={`matrix-row-${row.dimension_code}`}
                className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div>
                    <span className="font-medium text-slate-800">{row.dimension_code}</span>
                    {row.is_mandatory && (
                      <span className="ml-1 text-[10px] text-amber-600 bg-amber-50 px-1 rounded">G</span>
                    )}
                  </div>
                  <div className="text-slate-500 truncate max-w-[120px]">{row.dimension_name}</div>
                </td>
                {atypes.map(at => {
                  const cell = row.cells[at] || { rule_type: "OPTIONAL", source: "DEFAULT", rule_id: null };
                  const rule = ruleIndex[`${row.dimension_id}:${at}`];
                  return (
                    <td key={at} className="px-3 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <RuleBadge ruleType={cell.rule_type} source={cell.source} />
                        {canManage && cell.source !== "GLOBAL_MANDATORY" && (
                          <button
                            className="text-[10px] text-slate-400 hover:text-indigo-600 mt-0.5"
                            onClick={() => rule ? onEditRule(rule) : onCreateRule(row.dimension_id, at)}
                            data-testid={`matrix-cell-action-${row.dimension_code}-${at}`}
                          >
                            {rule ? "edit" : "+ rule"}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
